import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Truck, Users, FileText, AlertTriangle, CheckCircle, Zap, Fuel, X, UserPlus, Check, Search } from 'lucide-react';
import { openPalette } from '../components/CommandPalette';
import { duePeriodEnd, isDismissed, dismissForPeriod } from '../utils/fortnightDue';
import ToDoList from '../components/ToDoList';
import LeaveTodayBanner from '../components/LeaveTodayBanner';

export default function DashboardPage() {
  const { personnel, vehicles, documents, sheetLoading, pendingOnboarding, dismissPendingOnboarding, currentUser, addNotification, applicantInfoAlertCount, applicantInfoAlertNames, leaveEntries } = useApp();
  const isViewer = currentUser?.role === 'viewer';
  const navigate = useNavigate();

  // ── Personal to-do access ──────────────────────────────────────────────────
  const me = (currentUser?.login || '').toUpperCase();
  const [todoAccess, setTodoAccess] = useState(null);  // { owner, allowed: [] }
  useEffect(() => {
    fetch('/.netlify/functions/todos-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getToDoAccess' }),
    }).then(r => r.json()).then(d => { if (d.success) setTodoAccess(d); }).catch(() => {});
  }, []);
  const todoOwner   = (todoAccess?.owner || 'BOUVENGP2').toUpperCase();
  const isTodoOwner = me === todoOwner;
  const canSeeTodo  = isTodoOwner || (todoAccess?.allowed || []).map(x => x.toUpperCase()).includes(me);
  const jaydaAllowed = (todoAccess?.allowed || []).map(x => x.toUpperCase()).includes('BOUVENGJ');
  const toggleJayda  = async () => {
    try {
      const res = await fetch('/.netlify/functions/todos-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setToDoAccess', by: me, login: 'BOUVENGJ', enabled: !jaydaAllowed }),
      });
      const d = await res.json();
      if (d.success) { setTodoAccess(d); addNotification(`To-do ${!jaydaAllowed ? 'enabled' : 'turned off'} for Jayda (BOUVENGJ)`, 'success'); }
      else addNotification(d.error || 'Could not change access', 'error');
    } catch { addNotification('Could not change access', 'error'); }
  };

  const periodEnd = duePeriodEnd();
  const [fuelBannerDismissed, setFuelBannerDismissed] = useState(() => isDismissed(periodEnd));

  function handleDismissFuelBanner() {
    dismissForPeriod(periodEnd);
    setFuelBannerDismissed(true);
  }

  const drivers = personnel.filter(p => p.type === 'driver');
  const activeDrivers = drivers.filter(d => d.status === 'active').length;
  // Retired vans keep their row in Fleet (docs + service history are keyed by rego)
  // but must never be counted or alerted on — a traded-in van's expired rego can
  // never be renewed, so its alert would sit here for ever.
  const liveVehicles = vehicles.filter(v => !v.archived);
  const availableVehicles = liveVehicles.filter(v => v.status === 'available').length;
  const maintenanceVehicles = liveVehicles.filter(v => v.status === 'maintenance').length;
  const expiredDocs = documents.filter(d => d.status === 'expired').length;
  const reviewDocs = documents.filter(d => d.status === 'review').length;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const daysUntil = d => Math.ceil((new Date(d) - startOfToday) / 86400000);
  const dueLabel = days =>
    days < 0  ? `Overdue ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`
    : days === 0 ? 'Due today'
    : days === 1 ? 'Due tomorrow'
    : `Due in ${days} days`;

  const alerts = [
    // Pending onboarding from Import Parcels — actionable now, pinned first
    ...pendingOnboarding.map(item => ({
      type: 'onboarding',
      msg: `${item.login} — scanned in an import but not yet onboarded in Personnel`,
      login: item.login,
      action: () => navigate('/personnel'),
    })),
    ...liveVehicles.filter(v => daysUntil(v.rego_exp) < 60).map(v => ({
      type: daysUntil(v.rego_exp) <= 14 ? 'critical' : 'warning',
      msg: `${v.rego} rego expires ${new Date(v.rego_exp).toLocaleDateString('en-AU')}`,
      dueDays: daysUntil(v.rego_exp),
      due: dueLabel(daysUntil(v.rego_exp)),
      action: () => navigate('/fleet'),
    })),
    ...liveVehicles.filter(v => daysUntil(v.insurance_exp) < 30).map(v => ({
      type: 'critical',
      msg: `${v.rego} insurance expires ${new Date(v.insurance_exp).toLocaleDateString('en-AU')}`,
      dueDays: daysUntil(v.insurance_exp),
      due: dueLabel(daysUntil(v.insurance_exp)),
      action: () => navigate('/fleet'),
    })),
    ...documents.filter(d => d.status === 'expired').map(d => ({
      type: 'critical',
      msg: `Document expired: ${d.title}`,
      dueDays: -9999, // already expired — sort to the very top of the dated list
      due: 'Expired',
      action: () => navigate('/documents'),
    })),
  ].sort((a, b) => {
    // Onboarding items (no date) stay on top; everything else soonest-due first.
    const aDated = a.dueDays !== undefined, bDated = b.dueDays !== undefined;
    if (aDated !== bDated) return aDated ? 1 : -1;
    return (a.dueDays || 0) - (b.dueDays || 0);
  });

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Dashboard</h2>
          <p>Hollypaul Recruitment — Operations Overview</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            onClick={openPalette}
            className="form-input"
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: 230, cursor: 'pointer', color: 'var(--hp-dim)', textAlign: 'left', fontSize: '0.8rem' }}
          >
            <Search size={14} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>Search anything…</span>
            <kbd className="palette-kbd">⌘K</kbd>
          </button>
          {!isViewer && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/rcti')}>
                <Zap size={14} /> New RCTI Run
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/personnel')}>
                <Users size={14} /> Add Driver
              </button>
            </>
          )}
        </div>
      </div>

      {applicantInfoAlertCount > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '10px 16px',
        }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,.25)', flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: '.82rem', lineHeight: 1.4 }}>
            <strong>{applicantInfoAlertCount} applicant{applicantInfoAlertCount !== 1 ? 's' : ''} waiting for review</strong>
            {applicantInfoAlertNames && applicantInfoAlertNames.length > 0 ? ` — ${applicantInfoAlertNames.join(', ')}` : ''}
          </div>
          <button
            className="btn btn-primary btn-sm"
            style={{ flexShrink: 0, fontSize: '.75rem', background: '#ef4444', borderColor: '#ef4444' }}
            onClick={() => navigate('/personnel?tab=applicants')}
          >
            <Users size={13} /> Review
          </button>
        </div>
      )}

      <LeaveTodayBanner entries={leaveEntries} />

      {pendingOnboarding.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
          background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.35)',
          borderRadius: 8, padding: '10px 16px',
        }}>
          <span style={{ fontSize: '1.1rem' }}>👤</span>
          <div style={{ flex: 1, fontSize: '.82rem', lineHeight: 1.4 }}>
            <strong>{pendingOnboarding.length} driver{pendingOnboarding.length !== 1 ? 's' : ''} need{pendingOnboarding.length === 1 ? 's' : ''} onboarding</strong>
            {' '}— {pendingOnboarding.map(p => p.login).join(', ')} {pendingOnboarding.length === 1 ? 'was' : 'were'} found in a parcel import but {pendingOnboarding.length === 1 ? 'is' : 'are'} not yet in Personnel.
          </div>
          <button
            className="btn btn-primary btn-sm"
            style={{ flexShrink: 0, fontSize: '.75rem', background: '#6366f1', borderColor: '#6366f1' }}
            onClick={() => navigate('/personnel')}
          >
            <Users size={13} /> Add to Personnel
          </button>
        </div>
      )}

      {periodEnd && !fuelBannerDismissed && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.35)',
          borderRadius: 8, padding: '10px 16px', margin: '0 0 0 0',
        }}>
          <Fuel size={16} color="var(--hp-amber)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: '.82rem', lineHeight: 1.4 }}>
            <strong>Fuel assessment due</strong> — period ended{' '}
            {periodEnd.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}.
            Run the RTCCO fuel calculation before generating RCTIs.
          </div>
          <button
            className="btn btn-primary btn-sm"
            style={{ flexShrink: 0, fontSize: '.75rem' }}
            onClick={() => navigate('/fuel-workbench')}
          >
            <Fuel size={13} /> Open Fuel Workbench
          </button>
          <button
            onClick={handleDismissFuelBanner}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 4, flexShrink: 0 }}
            title="Dismiss until next period"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="page-body">

        {sheetLoading && (
          <div style={{ background: 'rgba(201,168,76,.06)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            Loading driver data from Google Sheets...
          </div>
        )}

        <div className="stat-grid">
          <div className="stat-card" style={{ '--accent': 'var(--hp-gold)' }}>
            <div className="stat-label">Active Drivers</div>
            <div className="stat-value">{activeDrivers}</div>
            <div className="stat-sub">of {drivers.length} total subcontractors</div>
          </div>
          <div className="stat-card" style={{ '--accent': 'var(--hp-blue)' }}>
            <div className="stat-label">Fleet Vehicles</div>
            <div className="stat-value">{liveVehicles.length}</div>
            <div className="stat-sub">{availableVehicles} available · {maintenanceVehicles} in maintenance</div>
          </div>
          <div className="stat-card" style={{ '--accent': 'var(--hp-green)' }}>
            <div className="stat-label">Total Subcontractors</div>
            <div className="stat-value">{drivers.length}</div>
            <div className="stat-sub">Go to RCTI page to run payroll</div>
          </div>
          <div className="stat-card" style={{ '--accent': expiredDocs > 0 ? 'var(--hp-red)' : 'var(--hp-purple)' }}>
            <div className="stat-label">Document Compliance</div>
            <div className="stat-value">{documents.filter(d => d.status === 'current').length}/{documents.length}</div>
            <div className="stat-sub">{expiredDocs} expired · {reviewDocs} due for review</div>
          </div>
        </div>

        {canSeeTodo && (
          <div style={{ marginBottom: 24 }}>
            <ToDoList
              login={me}
              headerAction={isTodoOwner ? (
                <button className="btn btn-ghost btn-sm" onClick={toggleJayda}
                  title={jaydaAllowed ? 'Jayda (BOUVENGJ) can use the to-do list — click to turn off' : 'Give Jayda (BOUVENGJ) her own private to-do list'}>
                  {jaydaAllowed ? <><Check size={13} /> Jayda allowed</> : <><UserPlus size={13} /> Allow Jayda</>}
                </button>
              ) : null}
            />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '24px' }}>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h4>Alerts & Actions</h4>
              <span className="badge badge-red">{alerts.length} items</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {alerts.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hp-green)', padding: '12px 0' }}>
                  <CheckCircle size={16} /> <span style={{ fontSize: '0.875rem' }}>All clear — no alerts</span>
                </div>
              ) : alerts.slice(0, 8).map((a, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px',
                  background: a.type === 'onboarding' ? 'rgba(99,102,241,0.06)'
                    : a.type === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)',
                  border: `1px solid ${a.type === 'onboarding' ? 'rgba(99,102,241,0.25)'
                    : a.type === 'critical' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
                  borderRadius: 'var(--radius)',
                }}>
                  {a.type === 'onboarding'
                    ? <UserPlus size={14} color="#6366f1" style={{ flexShrink: 0 }} />
                    : <AlertTriangle size={14} color={a.type === 'critical' ? 'var(--hp-red)' : 'var(--hp-amber)'} style={{ flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.8rem', color: 'var(--hp-text)', lineHeight: '1.4', flex: 1 }}>{a.msg}</span>
                  {a.due && (
                    <span style={{
                      flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, padding: '3px 9px',
                      borderRadius: 999, whiteSpace: 'nowrap',
                      background: a.dueDays <= 7 ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                      color: a.dueDays <= 7 ? 'var(--hp-red)' : 'var(--hp-amber)',
                    }}>{a.due}</span>
                  )}
                  {a.type === 'onboarding' ? (
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button onClick={a.action} style={{ fontSize:11, fontWeight:700, padding:'4px 10px', background:'#6366f1', color:'#fff', border:'none', borderRadius:'var(--radius)', cursor:'pointer', whiteSpace:'nowrap' }}>
                        Add to Personnel →
                      </button>
                      <button onClick={() => dismissPendingOnboarding(a.login)} title="Dismiss" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--hp-dim)', padding:'4px' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={a.action} style={{ fontSize:11, fontWeight:600, padding:'4px 10px', background:'transparent', color:'var(--hp-dim)', border:'1px solid var(--hp-border)', borderRadius:'var(--radius)', cursor:'pointer', flexShrink:0 }}>
                      View →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h4 style={{ marginBottom: '20px' }}>Quick Actions</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'Process RCTI Run', sub: 'Sync earnings from Google Sheets → Xero', icon: Zap, color: 'var(--hp-gold)', path: '/rcti' },
                { label: 'Issue Vehicle Lease Contract', sub: 'Generate Hollypaul Leasing agreement', icon: FileText, color: 'var(--hp-blue)', path: '/contracts' },
                { label: 'Add New Driver', sub: 'Onboard with subcontractor agreement', icon: Users, color: 'var(--hp-green)', path: '/personnel' },
                { label: 'Fleet Compliance Check', sub: 'Review rego, insurance & service due dates', icon: Truck, color: 'var(--hp-purple)', path: '/fleet' },
                { label: 'Upload Document', sub: 'Add to compliance document library', icon: FileText, color: 'var(--hp-amber)', path: '/documents' },
              ].map(({ label, sub, icon: Icon, color, path }) => (
                <button key={path} className="btn btn-ghost"
                  style={{ justifyContent: 'flex-start', gap: '14px', padding: '12px 14px', textAlign: 'left' }}
                  onClick={() => navigate(path)}>
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '8px',
                    background: `rgba(${color === 'var(--hp-gold)' ? '201,168,76' : color === 'var(--hp-blue)' ? '59,130,246' : color === 'var(--hp-green)' ? '34,197,94' : color === 'var(--hp-purple)' ? '139,92,246' : '245,158,11'},0.12)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon size={16} color={color} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{label}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{sub}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card mt-6">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h4>Driver Roster</h4>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/personnel')}>View all →</button>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Login Name</th>
                <th>Personal Name</th>
                <th>Company</th>
                <th>ABN</th>
                <th>Phone</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drivers.slice(0, 10).map(d => (
                <tr key={d.id}>
                  <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--hp-gold)' }}>{d.loginName}</span></td>
                  <td>{d.name}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{d.companyName || '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{d.abn || '—'}</td>
                  <td style={{ fontSize: '0.8rem' }}>{d.phone || '—'}</td>
                  <td><span className={`badge ${d.status === 'active' ? 'badge-green' : 'badge-dim'}`}>{d.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          {drivers.length > 10 && (
            <div style={{ textAlign: 'center', padding: '12px', fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              Showing 10 of {drivers.length} drivers — <button className="btn btn-ghost btn-sm" onClick={() => navigate('/personnel')}>View all</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
